import React from 'react';
import { Check } from 'react-feather';
import { Link } from 'react-router-dom';
import ENUM_TYPES from '@components-shared/enum.types';
import { GENERAL_CONSTANTS, ILocaleStructure } from '@components-shared/general.constants';
import { NotificationDialog } from '@components-shared/modal';
import { getCulture } from '@locales/helpers';
import { keyMap, KeyMapKeys } from '@locales/lokalise/keyMap';
import Big from 'big.js';
import cn from 'classnames';
import countryData, { Country } from 'country-telephone-data';
import { CURRENCY } from 'global.enums';
import { IAccountOption, LanguageType } from 'global.interfaces';
import i18next from 'i18next';
import { handleError } from './errors';
import { createFakeLinkAndDownloadFile, getFileUrlForDownloading } from './fileDownload';
import { NameValuePair, valueFromEnumService } from './library/getValueFromEnum.service';

interface countryDataExt {
  allCountries: Country[];
  allCountryCodes: { [dialCode: string]: string[] };
}

interface IArgObject {
  [name: string]: string;
}

interface INestedArgObject {
  [name: string]: string | IArgObject;
}

interface Money {
  value: number;
  currency: string;
}

interface PreciseMoney extends Money {
  precision: number;
}

export const copyToClipboard = (text: string): void => {
  if (window.clipboardData && window.clipboardData.setData) {
    window.clipboardData.setData('Text', text);
  }

  if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
    const textarea = document.createElement('textarea');

    textarea.textContent = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (error) {
      handleError('Copy to clipboard failed.', error);
    } finally {
      document.body.removeChild(textarea);
    }
  }
};

/* TODO Experimental Feature
 *  Added to test the new clipboard API.
 *  If testing is successful, the old function will be removed.
 *  */

export const copyToClipboardTest = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    NotificationDialog.show(keyMap.successfullyCopiedToClipboard, 'success');
  } catch (error) {
    handleError(keyMap.copyToClipboardFailed, error);
  }
};

export const getURLParams = <ParamNames extends string>(
  search?: string | null,
): {
  [k in ParamNames]?: string;
} => {
  const searchString = search || window.location.search;

  return searchString
    .replace('?', '')
    .split('&')
    .reduce((p, e) => {
      const a = e.split('=');
      let key;
      let value;

      try {
        key = decodeURIComponent(a[0]);
      } catch {
        key = a[0];
      }

      try {
        value = decodeURIComponent(a[1]);
      } catch {
        value = a[1];
      }

      return Object.assign({ ...p }, { [key]: value });
    }, {});
};

/**
 * @deprecated
 * Please use valueFromEnumService instead
 */
export const { getValueFromEnums, getValueFromEnumsOrText, getMultiValueFromEnum, getObjectFromEnums } =
  valueFromEnumService;

export const getPrettyJson = (value: string): string | JSX.Element | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    const output = JSON.stringify(parsed, null, 2);

    return <pre>{output}</pre>;
  } catch (e) {
    return value;
  }
};

/**
 * @deprecated Use only with legacy grid.
 * For new grids, please use the TagStatus component instead.
 */

export const getTableLabel = (value: string, enumConstant: Array<NameValuePair>): JSX.Element => {
  const columnValue = enumConstant ? getValueFromEnums(value, enumConstant) : value;

  return (
    <span className={`table__label ${value && value.toLowerCase ? value.toLowerCase() : value}`}>{columnValue}</span>
  );
};

export const getLinkedTableLabel = (value: string, link = '/', enumConstant: Array<NameValuePair>) => {
  const columnValue = enumConstant ? getValueFromEnums(value, enumConstant) : value;

  return (
    <Link className="link-btn" to={link}>
      <span className={cn('table__label', value?.toLowerCase?.())}>{columnValue}</span>
    </Link>
  );
};

export const getColoredValueFromEnums = (value: string, enumConstant: Array<NameValuePair>): JSX.Element => {
  const columnValue = enumConstant ? getValueFromEnums(value, enumConstant) : value;

  return (
    <span className={`colored_value ${value && value.toLowerCase ? value.toLowerCase() : value}`}>{columnValue}</span>
  );
};

export const getBooleanValue = (value: boolean | number): JSX.Element => {
  return value ? <Check size={14} /> : <React.Fragment></React.Fragment>;
};

export const onDownload = (url: string): boolean => {
  const form = document.createElement('form');

  form.method = 'POST';
  form.action = url;
  document.body.appendChild(form);

  try {
    form.submit();
    return true;
  } catch (error) {
    handleError('Download failed', error);
    return false;
  } finally {
    document.body.removeChild(form);
  }
};

export const onDownloadFile = async (apiUrl: string) => {
  try {
    const { url, fileName } = await getFileUrlForDownloading(apiUrl, undefined);

    createFakeLinkAndDownloadFile({ url, fileName });
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Download error:', error);
    handleError('Download failed', error);
    return false;
  }
};

export const getUpperFormattedInteger = (val: number | undefined): string => {
  if (val === undefined || val === null || isNaN(val)) {
    return '';
  }

  return getFormattedNumber(Math.ceil(val), { precision: 0 });
};

type FormattedNumberPrecision = {
  precision: true | number;
};

type FormattedNumberCurrency = {
  currency?: string;
  currencyPrecision?: number;
};

type FormattedNumberOptions = FormattedNumberPrecision | FormattedNumberCurrency;

export const getFormattedNumber = (val: number | undefined | null, options: FormattedNumberOptions = {}): string => {
  if (val === undefined || val === null || isNaN(val)) {
    return '';
  }
  const language = getCulture();

  const value = new Big(val);

  if ('precision' in options) {
    const { precision: precisionParameter } = options;
    const precision = precisionParameter === true ? GENERAL_CONSTANTS.DEFAULT_NUMBER_PRECISION : precisionParameter;

    const flooredValue = getFlooredValue(value, precision);

    return flooredValue.toNumber().toLocaleString(language, {
      maximumFractionDigits: 20,
      minimumFractionDigits: precision,
    });
  }

  if (options.currency) {
    const currencyDefaultPrecision = 2;
    const currencyPrecision = options.currencyPrecision ?? currencyDefaultPrecision;

    const currency = checkCurrencyInLocaleStringMethod(value.toNumber(), language, options.currency)
      ? options.currency
      : CURRENCY.USD;

    const flooredValue = getFlooredValue(value, currencyPrecision);

    return flooredValue.toNumber().toLocaleString(language, {
      style: 'currency',
      currency,
      maximumFractionDigits: 20,
      minimumFractionDigits: currencyPrecision,
    });
  }

  return i18next.t(keyMap.formatsFormatNumber, { value: value.toNumber() });
};

const getFlooredValue = (value: Big, precision: number) => {
  const shiftValue = new Big(10).pow(precision);
  const multipliedValue = value.times(shiftValue);

  return multipliedValue.round(0, 0).div(shiftValue);
};

export const numberFormat = (value: number | Money | PreciseMoney, format: string, lng?: string): string => {
  if (format === 'number') {
    return value.toLocaleString(lng);
  }

  if (format === 'money') {
    const moneyValue = value as Money;

    return moneyValue.value.toLocaleString(lng, {
      style: 'currency',
      currency: moneyValue.currency,
    });
  }

  if (format === 'float') {
    const floatValue = value as PreciseMoney;

    return floatValue.value.toLocaleString(lng, {
      maximumFractionDigits: floatValue.precision,
      minimumFractionDigits: floatValue.precision,
    });
  }

  return value.toString();
};

export const getArgumentsTable = (args: IArgObject = {}): JSX.Element => (
  <div className="summary-table">{Object.keys(args).map((a) => renderTableRow(a, args[a]))}</div>
);

export const renderNestedArguments = (value: string | null) => {
  if (!value) {
    return null;
  }

  try {
    return getNestedArgumentsTable(JSON.parse(value));
  } catch (e) {
    return value;
  }
};

export const getNestedArgumentsTable = (args: INestedArgObject = {}): JSX.Element => (
  <div className="summary-table">
    {Object.keys(args).map((a) => {
      if (typeof args[a] === 'object' && args[a] !== null) {
        return (
          <div className="summary-table__row" key={a}>
            {renderTableRowLabel(a)}
            <div className="summary-table__value">{getNestedArgumentsTable(args[a] as IArgObject)}</div>
          </div>
        );
      }

      return renderTableRow(a, args[a] as string);
    })}
  </div>
);

const renderTableRow = (label: string, value: string) => (
  <div className="summary-table__row" key={label}>
    {renderTableRowLabel(label)}
    <div className="summary-table__value">{getTableRowValue(value)}</div>
  </div>
);

const getTableRowValue = (value: unknown | boolean | string): JSX.Element | string | null => {
  if (typeof value === 'object' && value !== null) {
    return null;
  }

  if (typeof value === 'boolean') {
    return value.toString();
  }

  return value as string;
};

const renderTableRowLabel = (label: string) => (
  <div className="summary-table__label">
    {label
      .split(/([A-Z][a-z]+)/)
      .filter((e) => e)
      .join(' ')}
  </div>
);

export const getLink = (title: string | number | null | JSX.Element = '', link = '/', className = ''): JSX.Element => {
  if (!title) {
    return <></>;
  }
  return (
    <Link className={cn('link-btn', className)} to={link}>
      {title}
    </Link>
  );
};

export const getVefificationFieldColor = (status?: ENUM_TYPES['PSP_OPERATION_FIELD_VERIFICATION_STATUS']) => {
  switch (status) {
    case 'Unknown':
    case 'Failed':
      return 'summary-table__row--invalid';
    case 'Verified':
    case 'VerifiedByPreviousOperationsCompletedByAdmin':
      return 'summary-table__row--valid';
    default:
      return undefined;
  }
};

export const getExternalLink = (title: number | string | JSX.Element = '', link = '/', className = ''): JSX.Element => (
  <a className={cn('link-btn', className)} href={link} target="_blank" rel="noopener noreferrer">
    {title}
  </a>
);

export const getSubmitButton = (value: string): JSX.Element => (
  <div className="submit-btn">{i18next.t(value as KeyMapKeys)}</div>
);

export const getImagePreview = (src: string): JSX.Element => <img className="img-preview" src={src} alt="" />;

const getFilteredCountries = (excludedCountries: string[]): Country[] => {
  const { allCountries, allCountryCodes } = countryData as unknown as countryDataExt;

  if (excludedCountries.length === 0) {
    return [];
  }

  return allCountries.filter((country: Country) => {
    const countriesIsoWithSuchDialCode = allCountryCodes[country.dialCode];
    const isCurrentCountryIsoValid = countriesIsoWithSuchDialCode.includes(country.iso2);
    const isCurrentCountryExcluded = excludedCountries.includes(country.iso2);

    return isCurrentCountryIsoValid && !isCurrentCountryExcluded;
  });
};

export const guessSelectedCountryByPhoneNumber = (
  inputNumber: string,
  params: { defaultCountry?: string; excludedCountries: string[]; preferredCountries: string[] },
): string => {
  const { allCountries, allCountryCodes } = countryData as unknown as countryDataExt;
  const { defaultCountry, excludedCountries } = params;

  const pureInputNumber = inputNumber.replace('+', '');
  const pureInputNumberDialCode = pureInputNumber.substr(0, 4);

  const filteredAllCountries = getFilteredCountries(excludedCountries);
  let secondBestGuess = allCountries.find((c) => c.iso2 === defaultCountry);

  if (!secondBestGuess && filteredAllCountries) {
    secondBestGuess = filteredAllCountries[0];
  }

  const replaceAllCountryNamesWithNativeLanguage = (name: string) => name.replace(/\s\((.*?)$/g, '');

  const formatCountryName = (countryObject: Country) =>
    countryObject
      ? replaceAllCountryNamesWithNativeLanguage(countryObject.name).replace(/\s/g, '').replace('’', '')
      : '';

  const getBestGuessCountry = (): Country => {
    const bestGuessInitialValue: Country = {
      name: '',
      iso2: '',
      dialCode: '',
      format: '',
      priority: 10001,
    };

    return filteredAllCountries.reduce(function (selectedCountry, country) {
      const countryIsoByDialCode = allCountryCodes[pureInputNumberDialCode];

      if (countryIsoByDialCode) {
        if (countryIsoByDialCode[0] === country.iso2) {
          return country;
        } else if (countryIsoByDialCode[0] === selectedCountry.iso2) {
          return selectedCountry;
        }
      }

      const numberStartsWithDialCode = pureInputNumber.startsWith(country.dialCode);

      if (numberStartsWithDialCode) {
        if (country.dialCode.length > selectedCountry.dialCode.length) {
          return country;
        }

        if (
          country.dialCode.length === selectedCountry.dialCode.length &&
          country.priority < selectedCountry.priority
        ) {
          return country;
        }
      }

      return selectedCountry;
    }, bestGuessInitialValue);
  };

  const bestGuess = getBestGuessCountry();

  if (bestGuess && bestGuess.name) {
    return formatCountryName(bestGuess);
  } else {
    return formatCountryName(secondBestGuess as Country);
  }
};

export const openLink = (url: string, target?: '_blank'): void => {
  const a = document.createElement('a');

  a.href = url;
  if (target) {
    a.target = target;
  }
  a.click();
  a.remove();
};

const checkCurrencyInLocaleStringMethod = (
  value: { toLocaleString(s: string, l: unknown): string },
  language: string,
  currency: string,
): boolean => {
  try {
    value.toLocaleString(language, {
      style: 'currency',
      currency,
    });

    return true;
  } catch {
    return false;
  }
};

export const getLocaleByLanguage = (locale: LanguageType): ILocaleStructure => {
  const { LOCALES } = GENERAL_CONSTANTS;
  const localesKey = locale.replace('-', '_').toLocaleUpperCase() as LanguageType;

  return LOCALES[localesKey];
};

export const getAccountOptions = (accounts: IAccountOption[]): IAccountOption[] => {
  const options = accounts.filter((acc) => acc.currency);

  return options.map((option) =>
    Object.assign(
      {},
      {
        ...option,
        title: `${option.login}`,
        value: `${option.login}`,
        walletType: null,
        accountType: 'account',
      },
    ),
  );
};

export const getTwoCharsInitials = (firstName: string, lastName: string): string =>
  `${firstName?.length > 0 ? firstName[0] : ''}${lastName?.length > 0 ? lastName[0] : ''}`;
